import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { ROLES, ROLE_LABELS } from "@/lib/constants";
import { Users, Briefcase, FileText, TrendingUp, Trash2 } from "lucide-react";
import AdminAnalytics from "@/components/dashboards/AdminAnalytics";
import AdminVerifications from "@/components/dashboards/AdminVerifications";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [error, setError] = useState("");
  const [freePosting, setFreePosting] = useState(true);
  const [commissionRate, setCommissionRate] = useState(10);
  const [withdrawals, setWithdrawals] = useState([]);
  const [adsEnabled, setAdsEnabled] = useState(true);
  const [adCampaigns, setAdCampaigns] = useState([]);

  const loadAds = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([
        api.get("/ads/admin/settings"),
        api.get("/ads/admin/campaigns"),
      ]);
      setAdsEnabled(s.data.enabled);
      setAdCampaigns(c.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  const toggleAds = async () => {
    setError("");
    try {
      const res = await api.patch("/ads/admin/settings", { enabled: !adsEnabled });
      setAdsEnabled(res.data.enabled);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const moderateCampaign = async (campaignId, status) => {
    setError("");
    try {
      await api.post(`/ads/admin/campaigns/${campaignId}/status`, { status });
      loadAds();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const loadWithdrawals = useCallback(async () => {
    try {
      const res = await api.get("/wallet/admin/withdrawals");
      setWithdrawals(res.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  const saveCommission = async () => {
    setError("");
    try {
      const res = await api.patch("/admin/settings", {
        free_posting: freePosting,
        commission_rate: parseFloat(commissionRate) / 100,
      });
      setCommissionRate(res.data.commission_rate * 100);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const setWithdrawalStatus = async (requestId, status) => {
    setError("");
    try {
      await api.post(`/wallet/admin/withdrawals/${requestId}/status`, { status });
      loadWithdrawals();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const loadSettings = useCallback(async () => {
    try {
      const res = await api.get("/admin/settings");
      setFreePosting(res.data.free_posting);
      setCommissionRate(Math.round((res.data.commission_rate ?? 0.1) * 10000) / 100);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  const toggleFreePosting = async () => {
    try {
      const res = await api.patch("/admin/settings", { free_posting: !freePosting });
      setFreePosting(res.data.free_posting);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const loadStats = useCallback(async () => {
    try {
      const res = await api.get("/admin/stats");
      setStats(res.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const params = {};
      if (search) params.q = search;
      if (roleFilter) params.role = roleFilter;
      const res = await api.get("/admin/users", { params });
      setUsers(res.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, [search, roleFilter]);

  useEffect(() => {
    loadStats();
    loadSettings();
    loadWithdrawals();
    loadAds();
  }, [loadStats, loadSettings, loadWithdrawals, loadAds]);

  useEffect(() => {
    const t = setTimeout(loadUsers, 300);
    return () => clearTimeout(t);
  }, [loadUsers]);

  const changeRole = async (userId, role) => {
    setError("");
    try {
      await api.patch(`/admin/users/${userId}/role`, { role });
      loadUsers();
      loadStats();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const deleteUser = async (userId) => {
    setError("");
    try {
      await api.delete(`/admin/users/${userId}`);
      loadUsers();
      loadStats();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const statCards = stats
    ? [
        { label: "Utilisateurs", value: stats.total_users, icon: Users, testid: "stat-total-users", to: "/admin/listes/utilisateurs" },
        { label: "Candidats", value: stats.users_by_role?.candidat || 0, icon: Users, testid: "stat-candidats", to: "/admin/listes/candidats" },
        { label: "Employeurs", value: (stats.users_by_role?.employeur || 0) + (stats.users_by_role?.manager_employeur || 0), icon: Briefcase, testid: "stat-employeurs", to: "/admin/listes/employeurs" },
        { label: "Offres actives", value: stats.active_offers, icon: TrendingUp, testid: "stat-active-offers", to: "/admin/listes/offres" },
        { label: "CVs", value: stats.total_cvs, icon: FileText, testid: "stat-total-cvs", to: "/admin/listes/cvs" },
        { label: "Candidatures", value: stats.total_applications || 0, icon: TrendingUp, testid: "stat-total-applications", to: "/admin/listes/candidatures" },
      ]
    : [];

  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="font-semibold text-brand-navy">Monétisation</h2>
            <p className="text-sm text-slate-500">
              {freePosting
                ? "La publication d'offres est actuellement gratuite pour les employeurs."
                : "La publication d'offres nécessite un crédit payant."}
            </p>
          </div>
          <button
            data-testid="monetization-toggle"
            onClick={toggleFreePosting}
            className={`relative w-14 h-7 rounded-full transition-colors duration-200 shrink-0 ${freePosting ? "bg-brand-turquoise" : "bg-brand-orange"}`}
            aria-label="Basculer la publication gratuite"
          >
            <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform duration-200 ${freePosting ? "left-0.5" : "translate-x-7"}`} />
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-slate-100">
          <div>
            <h3 className="text-sm font-semibold text-brand-navy">Commission plateforme (Market + factures en ligne)</h3>
            <p className="text-xs text-slate-500">Prélevée avant crédit du portefeuille du vendeur (0 à 50 %).</p>
          </div>
          <div className="flex items-center gap-2 sm:ml-auto">
            <input
              data-testid="commission-rate-input"
              type="number" min="0" max="50" step="0.5"
              value={commissionRate}
              onChange={(e) => setCommissionRate(e.target.value)}
              className="w-24 border border-slate-300 rounded-lg px-3 py-2 text-sm text-right focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
            <span className="text-sm text-slate-500">%</span>
            <button
              data-testid="commission-rate-save"
              onClick={saveCommission}
              className="text-xs font-semibold text-white bg-brand-navy rounded-lg px-4 py-2 hover:bg-brand-blue transition-colors"
            >
              Enregistrer
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-5 border-b border-slate-100">
          <h2 className="font-semibold text-slate-900">Demandes de retrait</h2>
          <p className="text-xs text-slate-500 mt-0.5">Traitement manuel hors plateforme (virement ou PayPal). Le montant est réservé dès la demande et remboursé en cas de rejet.</p>
        </div>
        {withdrawals.length === 0 ? (
          <p data-testid="admin-withdrawals-empty" className="px-5 py-8 text-center text-sm text-slate-400">Aucune demande de retrait.</p>
        ) : (
          withdrawals.map((w) => (
            <div key={w.request_id} data-testid={`admin-withdrawal-${w.request_id}`} className="flex flex-wrap items-center gap-3 px-5 py-3.5 border-b border-slate-50">
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm font-medium text-slate-900">{w.user_name} — {w.amount} $ {w.currency}</p>
                <p className="text-xs text-slate-400">{w.user_email} · {w.requested_at ? new Date(w.requested_at).toLocaleDateString("fr-CA") : ""}</p>
              </div>
              <span data-testid={`admin-withdrawal-status-${w.request_id}`} className={`text-xs font-medium rounded-full px-2.5 py-1 ${
                w.status === "demande" ? "bg-amber-50 text-amber-700"
                : w.status === "approuvee" ? "bg-blue-50 text-blue-700"
                : w.status === "payee" ? "bg-green-50 text-green-700"
                : "bg-red-50 text-red-600"
              }`}>
                {{ demande: "En attente", approuvee: "Approuvée", payee: "Payée", rejetee: "Rejetée" }[w.status]}
              </span>
              {w.status === "demande" && (
                <>
                  <button
                    data-testid={`admin-withdrawal-approve-${w.request_id}`}
                    onClick={() => setWithdrawalStatus(w.request_id, "approuvee")}
                    className="text-xs font-semibold text-white bg-brand-turquoise rounded-lg px-3 py-1.5 hover:bg-brand-navy"
                  >
                    Approuver
                  </button>
                  <button
                    data-testid={`admin-withdrawal-reject-${w.request_id}`}
                    onClick={() => setWithdrawalStatus(w.request_id, "rejetee")}
                    className="text-xs font-semibold text-red-600 border border-red-200 rounded-lg px-3 py-1.5 hover:bg-red-50"
                  >
                    Rejeter
                  </button>
                </>
              )}
              {w.status === "approuvee" && (
                <>
                  <button
                    data-testid={`admin-withdrawal-pay-${w.request_id}`}
                    onClick={() => setWithdrawalStatus(w.request_id, "payee")}
                    className="text-xs font-semibold text-white bg-brand-blue rounded-lg px-3 py-1.5 hover:bg-brand-navy"
                  >
                    Marquer payée
                  </button>
                  <button
                    data-testid={`admin-withdrawal-reject-${w.request_id}`}
                    onClick={() => setWithdrawalStatus(w.request_id, "rejetee")}
                    className="text-xs font-semibold text-red-600 border border-red-200 rounded-lg px-3 py-1.5 hover:bg-red-50"
                  >
                    Rejeter
                  </button>
                </>
              )}
            </div>
          ))
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="font-semibold text-slate-900">Publicité interne (NEXA Ads)</h2>
            <p className="text-xs text-slate-500 mt-0.5">Kill switch global : suspend instantanément toute diffusion publicitaire sur la plateforme.</p>
          </div>
          <div className="flex items-center gap-3">
            <span data-testid="ads-switch-state" className={`text-xs font-semibold ${adsEnabled ? "text-green-600" : "text-red-600"}`}>
              {adsEnabled ? "Diffusion active" : "Diffusion suspendue"}
            </span>
            <button
              data-testid="ads-kill-switch"
              onClick={toggleAds}
              className={`relative w-14 h-7 rounded-full transition-colors duration-200 shrink-0 ${adsEnabled ? "bg-brand-turquoise" : "bg-red-400"}`}
              aria-label="Kill switch publicité"
            >
              <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform duration-200 ${adsEnabled ? "left-0.5" : "translate-x-7"}`} />
            </button>
          </div>
        </div>
        {adCampaigns.length === 0 ? (
          <p data-testid="admin-ads-empty" className="px-5 py-8 text-center text-sm text-slate-400">Aucune campagne.</p>
        ) : (
          adCampaigns.map((c) => (
            <div key={c.campaign_id} data-testid={`admin-campaign-${c.campaign_id}`} className="flex flex-wrap items-center gap-3 px-5 py-3.5 border-b border-slate-50">
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm font-medium text-slate-900">{c.title}</p>
                <p className="text-xs text-slate-400">{c.owner_name} · {c.budget} $ {c.currency} · {c.impressions_count}/{c.impressions_limit} impressions · {c.clicks_count} clics</p>
              </div>
              <span className={`text-xs font-medium rounded-full px-2.5 py-1 ${
                c.status === "active" ? "bg-green-50 text-green-700"
                : c.status === "suspendue" ? "bg-red-50 text-red-600"
                : "bg-slate-100 text-slate-500"
              }`}>
                {c.status}
              </span>
              {c.status !== "suspendue" && c.paid && (
                <button
                  data-testid={`admin-campaign-suspend-${c.campaign_id}`}
                  onClick={() => moderateCampaign(c.campaign_id, "suspendue")}
                  className="text-xs font-semibold text-red-600 border border-red-200 rounded-lg px-3 py-1.5 hover:bg-red-50"
                >
                  Suspendre
                </button>
              )}
              {c.status === "suspendue" && (
                <button
                  data-testid={`admin-campaign-reactivate-${c.campaign_id}`}
                  onClick={() => moderateCampaign(c.campaign_id, "active")}
                  className="text-xs font-semibold text-white bg-brand-turquoise rounded-lg px-3 py-1.5 hover:bg-brand-navy"
                >
                  Réactiver
                </button>
              )}
            </div>
          ))
        )}
      </div>

      <AdminAnalytics />

      <AdminVerifications />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map(({ label, value, icon: Icon, testid, to }) => (
          <button key={testid} data-testid={testid} onClick={() => navigate(to)}
            className="bg-white rounded-xl border border-slate-200 p-5 text-left hover:border-brand-turquoise hover:shadow-sm transition-all cursor-pointer"
            title={`Voir la liste — ${label}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
              <Icon className="w-4 h-4 text-slate-400" />
            </div>
            <p className="text-2xl font-bold text-slate-900">{value}</p>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-5 border-b border-slate-100 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <h2 className="font-semibold text-slate-900">Gestion des utilisateurs</h2>
          <div className="flex gap-2">
            <input
              data-testid="admin-user-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher…"
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
            <select
              data-testid="admin-role-filter"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-900"
            >
              <option value="">Tous les rôles</option>
              {ROLES.map((r) => (
                <option key={r} value={r}>{ROLE_LABELS[r]}</option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <div data-testid="admin-error" className="mx-5 mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-100">
                <th className="px-5 py-3 font-medium">Nom</th>
                <th className="px-5 py-3 font-medium">Courriel</th>
                <th className="px-5 py-3 font-medium">Rôle</th>
                <th className="px-5 py-3 font-medium">Inscription</th>
                <th className="px-5 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="px-5 py-3 font-medium text-slate-900">{u.name}</td>
                  <td className="px-5 py-3 text-slate-600">{u.email}</td>
                  <td className="px-5 py-3">
                    <select
                      data-testid={`admin-role-select-${u.user_id}`}
                      value={u.role}
                      onChange={(e) => changeRole(u.user_id, e.target.value)}
                      className="border border-slate-200 rounded-md px-2 py-1 text-xs bg-white"
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-5 py-3 text-slate-500">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString("fr-CA") : "—"}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      data-testid={`admin-delete-user-${u.user_id}`}
                      onClick={() => deleteUser(u.user_id)}
                      className="text-slate-400 hover:text-red-600 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={5} data-testid="admin-users-empty" className="px-5 py-8 text-center text-slate-400">
                    Aucun utilisateur trouvé.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
